<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import { useResizeObserver, useScroll } from '@vueuse/core';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import { useReservationGuests } from '@/composition/reservations/use-reservation-guests';
  import { formatReservationPhone } from '@/domain/reservations/reservation-phone';
  import {
    formatDateLong,
    getReservationTimeZone,
  } from '@/domain/reservations/reservation-time';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import type { TReservationGuestSort } from '@/api/services/reservations.service';
  const emit = defineEmits<{ (event: 'guest', customerId: number): void }>();
  const restaurant = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;
  const {
    search,
    page,
    sortBy,
    sortDesc,
    guests,
    total,
    totalPages,
    loading,
    error,
    load,
    sort,
  } = useReservationGuests();
  const timezone = computed(() =>
    getReservationTimeZone(
      restaurant.selectedBranch?.location,
      restaurant.restaurant?.country
    )
  );
  const lastVisitFormatter = computed(
    () =>
      new Intl.DateTimeFormat(proxy.$i18n.locale, {
        timeZone: timezone.value,
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
  );
  function lastVisit(value: string | null): string {
    return value ? lastVisitFormatter.value.format(new Date(value)) : '—';
  }
  const columns: {
    key: string;
    label: string;
    sort?: TReservationGuestSort;
  }[] = [
    { key: 'guest', label: 'reservations.guests.colGuest', sort: 'name' },
    {
      key: 'visits',
      label: 'reservations.guests.colVisits',
      sort: 'visitsCount',
    },
    { key: 'cancellations', label: 'reservations.guests.colCancellations' },
    {
      key: 'no-shows',
      label: 'reservations.guests.colNoShows',
      sort: 'noShowsCount',
    },
    {
      key: 'last-visit',
      label: 'reservations.guests.colLastVisit',
      sort: 'lastVisitAt',
    },
  ];
  // Phones cannot fit every column: the list scrolls sideways under a pinned
  // guest column. The edges tell the state: a hairline on the pinned column
  // once the rest moved under it, a fade on the right while more is hidden.
  const scrollerRef = ref<HTMLElement>();
  const { arrivedState, measure } = useScroll(scrollerRef);

  // The arrived state only refreshes on scroll events: re-measure when the
  // scroller (re)mounts with the list or changes size (no overflow anymore).
  useResizeObserver(scrollerRef, measure);
  defineExpose({ reload: load });
</script>
<template>
  <div class="reservation-guests-tab" :aria-busy="loading">
    <div class="guests-toolbar">
      <PosUiInput
        v-model="search"
        icon="magnify"
        clearable
        :placeholder="$t('reservations.guests.searchPlaceholder')"
        class="guest-search"
      />
      <span v-if="guests" class="guest-count">{{
        $t('reservations.guests.count', { count: total })
      }}</span>
    </div>
    <div v-if="error" class="load-error" role="alert">
      <span>{{ $t('reservations.guests.loadError') }}</span
      ><PosUiButton
        color="neutral"
        variant="outline"
        :disabled="loading"
        @click="load"
        >{{ $t('reservations.page.retry') }}</PosUiButton
      >
    </div>
    <div v-else-if="!guests" class="guest-skeletons">
      <PosUiSkeleton v-for="n in 5" :key="n" class="skeleton-row" />
    </div>
    <PosUiEmptyState
      v-else-if="!guests.length"
      icon="account-group-outline"
      :title="
        $t(
          search
            ? 'reservations.guests.emptySearchTitle'
            : 'reservations.guests.emptyTitle'
        )
      "
      :description="
        search ? undefined : $t('reservations.guests.emptyDescription')
      "
    />
    <template v-else>
      <div
        class="guests-list"
        :class="{
          'is-scrolled': !arrivedState.left,
          'has-more': !arrivedState.right,
        }"
      >
        <div ref="scrollerRef" class="guests-scroller">
          <div class="guests-list-header">
            <span
              v-for="column in columns"
              :key="column.key"
              :class="`col-${column.key}`"
            >
              <button
                v-if="column.sort"
                type="button"
                :class="{ active: sortBy === column.sort }"
                :aria-pressed="sortBy === column.sort"
                @click="sort(column.sort)"
              >
                {{ $t(column.label)
                }}<i
                  v-if="sortBy === column.sort"
                  class="mdi"
                  :class="sortDesc ? 'mdi-arrow-down' : 'mdi-arrow-up'"
                  aria-hidden="true"
                />
              </button>
              <span v-else>{{ $t(column.label) }}</span>
            </span>
          </div>
          <button
            v-for="guest in guests"
            :key="guest.customerId"
            type="button"
            class="guest-row"
            @click="emit('guest', guest.customerId)"
          >
            <span class="col-guest"
              ><span class="guest-name">{{ guest.name }}</span
              ><span class="guest-phone">{{
                formatReservationPhone(
                  guest.phoneE164,
                  restaurant.restaurant?.country ?? 'AR'
                ) || '—'
              }}</span></span
            >
            <span class="col-visits">{{ guest.visitsCount }}</span
            ><span class="col-cancellations">{{
              guest.cancellationsCount
            }}</span
            ><span class="col-no-shows">{{ guest.noShowsCount }}</span
            ><span
              class="col-last-visit"
              :title="
                guest.lastVisitAt
                  ? formatDateLong(guest.lastVisitAt, timezone, $i18n.locale)
                  : undefined
              "
              >{{ lastVisit(guest.lastVisitAt) }}</span
            >
          </button>
        </div>
      </div>
      <div v-if="totalPages > 1" class="guests-pager">
        <PosUiButton
          color="neutral"
          variant="outline"
          icon="chevron-left"
          :aria-label="$t('qr.preview.previous')"
          :disabled="page <= 1 || loading"
          @click="page -= 1"
        /><span>{{
          $t('reservations.guests.pager', { page, total: totalPages })
        }}</span
        ><PosUiButton
          color="neutral"
          variant="outline"
          icon="chevron-right"
          :aria-label="$t('qr.preview.next')"
          :disabled="page >= totalPages || loading"
          @click="page += 1"
        />
      </div>
    </template>
  </div>
</template>
<style scoped lang="scss">
  .reservation-guests-tab {
    @apply tw-mx-auto tw-flex tw-h-full tw-min-h-0 tw-w-full tw-max-w-7xl tw-flex-col tw-gap-3 tw-overflow-y-auto tw-p-4 md:tw-p-6;
    > * {
      @apply tw-shrink-0;
    }
    .guests-toolbar {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-3;
    }
    .guest-search {
      @apply tw-w-full tw-max-w-sm;
    }
    .guest-count {
      @apply tw-text-sm tw-tabular-nums tw-text-neutral-500;
    }
    .guest-skeletons {
      @apply tw-flex tw-flex-col tw-gap-2;
    }
    .skeleton-row {
      @apply tw-h-16 tw-rounded-xl;
    }
    .load-error {
      @apply tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-rounded-xl tw-border tw-border-red-200 tw-p-4 tw-text-sm tw-text-red-700;
    }
    .guests-list {
      @apply tw-relative tw-overflow-hidden tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white;
      // Right-edge fade while more columns hide past the edge.
      &::after {
        content: '';
        @apply tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-0 tw-w-10 tw-bg-gradient-to-l tw-from-white tw-to-transparent tw-opacity-0 tw-transition-opacity;
      }

      &.has-more::after {
        @apply tw-opacity-100;
      }
    }
    // Columns never collapse: when they do not fit (phones) the list scrolls
    // sideways and the guest column stays pinned on the left. Header and rows
    // size to their columns (min-w-max) so every row spans the whole
    // scrollable width; the padding lives in the first and last cells so the
    // pinned cell covers the rows sliding under it.
    .guests-scroller {
      @apply tw-overflow-x-auto;
    }
    .guests-list-header,
    .guest-row {
      @apply tw-flex tw-min-w-max tw-items-center tw-gap-3 tw-pr-4;
    }
    .guests-list-header {
      @apply tw-min-h-[44px] tw-border-b tw-border-neutral-200 tw-bg-canvas tw-text-xs tw-font-medium tw-leading-tight tw-text-neutral-500;
      // Long single-word labels ("Nichterscheinen") wrap inside their narrow column.
      > span {
        @apply tw-whitespace-normal tw-break-words;
      }
      button {
        @apply tw-inline-flex tw-min-h-[44px] tw-items-center tw-gap-1 tw-whitespace-normal tw-break-words tw-text-left;
        &.active {
          @apply tw-text-neutral-900;
        }
      }
    }
    // Pinned column: w-0 + flex-auto let it grow with the row on wide screens
    // while its min width (not the longest name) sets the row's max-content
    // width, so every row scrolls to the same width.
    .col-guest {
      @apply tw-sticky tw-left-0 tw-flex tw-w-0 tw-min-w-[11rem] tw-flex-auto tw-flex-col tw-justify-center tw-self-stretch tw-bg-inherit tw-pl-4 tw-text-left;
      // Edge hairline, shown while the other columns are scrolled under it.
      &::after {
        content: '';
        @apply tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-0 tw-w-px tw-bg-neutral-200 tw-opacity-0 tw-transition-opacity;
      }
    }
    .guests-list.is-scrolled .col-guest::after {
      @apply tw-opacity-100;
    }
    .col-visits,
    .col-no-shows {
      @apply tw-w-16 tw-flex-none tw-text-right;
    }
    .col-cancellations {
      @apply tw-w-16 tw-flex-none tw-text-right;
    }
    .col-last-visit {
      @apply tw-w-40 tw-flex-none tw-whitespace-nowrap tw-text-right;
    }
    .guest-row {
      @apply tw-relative tw-min-h-[64px] tw-w-full tw-border-b tw-border-neutral-100 tw-bg-white tw-text-sm tw-tabular-nums tw-text-neutral-700 last:tw-border-b-0 hover:tw-bg-neutral-100;
      .col-guest {
        @apply tw-py-3;
      }
      // The pinned cell paints over the row, so the focus ring goes on an
      // overlay above it.
      &:focus-visible {
        @apply tw-ring-0;
        &::after {
          content: '';
          @apply tw-pointer-events-none tw-absolute tw-inset-0 tw-z-[1] tw-ring-2 tw-ring-inset tw-ring-orange-500/40;
        }
      }
    }
    .guest-name {
      @apply tw-break-words tw-font-medium tw-text-neutral-900;
    }
    .guest-phone {
      @apply tw-text-xs tw-text-neutral-500;
    }
    .guests-pager {
      @apply tw-flex tw-items-center tw-justify-center tw-gap-3 tw-text-sm tw-tabular-nums tw-text-neutral-500;
    }
    button:focus-visible {
      @apply tw-outline-none tw-ring-2 tw-ring-inset tw-ring-orange-500/40;
    }
  }
</style>
